const throttle = require('lodash.throttle');

/**
 * In -> https://google.com/document#pay
 * Out -> pay
 */
export const parseAnchorHref = (href) => {
    if (!href) { return undefined; }

    const index = href.indexOf('#');

    if (index === -1) { return undefined; }

    return href.substring(index + 1);
};

export function setupToc () {
    const getRefs = () => {
        const wrapper = document.querySelector('.toc-wrapper');

        if (!wrapper) {
            return null;
        }

        const showToc = wrapper.querySelector('button.show-toc');
        const jumpLinks = wrapper.querySelectorAll('.toc li a');
        const goToTop = wrapper.querySelector('button.go-to-top');

        if (!showToc || !goToTop) {
            return null;
        }

        return {
            wrapper,
            showToc,
            jumpLinks,
            goToTop
        };
    };

    const refs = getRefs();

    if (!refs) {
        return;
    }

    const { jumpLinks } = refs;

    const getBoundsOfSection = (i) => {
        const jumpLink = jumpLinks[i];

        if (!jumpLink) {
            return null;
        }

        const anchorId = parseAnchorHref(jumpLink.getAttribute('href'));
        if (!anchorId) {
            return null;
        }

        const anchor = document.querySelector(`#${anchorId}`);
        if (!anchor) {
            return null;
        }

        const anchorBounds = anchor.getBoundingClientRect();

        if (i === jumpLinks.length - 1) {
            const containerBounds = anchor.parentElement.getBoundingClientRect();

            return {
                top: anchorBounds.top,
                bottom: containerBounds.bottom
            };
        } else {
            const nextJumpLink = jumpLinks[i + 1];
            const nextAnchorId = parseAnchorHref(nextJumpLink.getAttribute('href'));

            if (!nextAnchorId) {
                return null;
            }

            const nextAnchor = document.querySelector(`#${nextAnchorId}`);

            if (!nextAnchor) {
                return null;
            }

            return {
                top: anchorBounds.top,
                bottom: nextAnchor.getBoundingClientRect().top
            };
        }
    };

    const getToc = function () {
        const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

        const toc = [];

        // Find the associated anchor tags
        for (let i = 0; i < jumpLinks.length; i++) {
            const jumpLink = jumpLinks[i];
            // Compute the bounds of the achor and its contents (which is the distance between this anchor
            // and the next - which is the content in between the anchors)
            const bounds = getBoundsOfSection(i);
            const heightVisible = Math.min(viewportHeight, bounds.bottom) - Math.max(0, bounds.top);
            const isCompletelyVisible = bounds.top >= 0 && bounds.bottom <= viewportHeight;

            toc.push({
                jumpLink,
                heightVisible,
                isCompletelyVisible
            });
        }

        return toc;
    };

    const handleMobileNavScroll = () => {
        if (jumpLinks.length > 0) {
            const first = jumpLinks[0];
            const { wrapper } = refs;

            const anchorId = parseAnchorHref(first.getAttribute('href'));
            if (!anchorId) {
                return null;
            }

            const anchor = document.querySelector(`#${anchorId}`);
            if (!anchor) {
                return null;
            }

            const anchorBounds = anchor.getBoundingClientRect();

            if (anchorBounds.top < 0) {
                wrapper.classList.add('in-toc-content-viewport');
            } else {
                wrapper.classList.remove('in-toc-content-viewport');
            }
        }
    };

    const setupMobileJumpLinks = () => {
        const { wrapper } = refs;

        for (const jumpLink of jumpLinks) {
            jumpLink.addEventListener('click', e => {
                if (wrapper.getAttribute('aria-expanded') === 'true') {
                    wrapper.removeAttribute('aria-expanded');
                }
            });
        }
    };

    const processTocTracking = function () {
        const toc = getToc();

        // If a section is completely visible on screen, then we assume the user is viewing
        // this, then find the section with the most visible area on screen
        let j = -1;
        let largest = Number.MIN_VALUE;

        for (let i = 0; i < toc.length; i++) {
            const item = toc[i];

            if (item.isCompletelyVisible) {
                j = i;
                break;
            }

            const heightVisible = item.heightVisible;

            if (heightVisible > largest) {
                largest = heightVisible;
                j = i;
            }
        }

        // Remove the highlighting class from all but the jump link we're looking at
        for (let k = 0; k < toc.length; k++) {
            const jumpLink = toc[k].jumpLink;

            if (k === j) {
                jumpLink.classList.add('toc-highlight');
            } else {
                jumpLink.classList.remove('toc-highlight');
            }
        }

        handleMobileNavScroll();
    };

    // Create a debounced function around the TOC tracking function
    const throttledProcessTocTracking = throttle(processTocTracking, 200);

    // Call it on window scroll
    window.addEventListener('scroll', throttledProcessTocTracking);
    window.addEventListener('resize', throttledProcessTocTracking);

    // Update the TOC tracking immediately, as a scroll event may not have fired
    processTocTracking();

    setupMobileJumpLinks();

    refs.goToTop.addEventListener('click', () => {
        const { wrapper } = refs;

        window.scrollTo({
            top: 0
        });
        wrapper.removeAttribute('aria-expanded');
    });

    refs.showToc.addEventListener('click', e => {
        const { wrapper, showToc } = refs;

        if (wrapper.getAttribute('aria-expanded') === 'true') {
            wrapper.removeAttribute('aria-expanded');
            showToc.setAttribute('title', 'Show Table of Contents');
        } else {
            wrapper.setAttribute('aria-expanded', 'true');
            showToc.setAttribute('title', 'Hide Table of Contents');
        }
    });
}



// WEBPACK FOOTER //
// ./src/js/app/table-of-contents.js